import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useAuth, API } from "../../App";

/**
 * Small AI-regenerate button that hits POST /api/admin/seo/regenerate/{collection}/{docId}.
 * Backend saves the freshly generated LLM text to DB and returns it — parent can
 * mirror it into the edit form via the `onText` callback.
 *
 * Props:
 *  - collection: "news" | "players" | "chants" | "fan_clubs"
 *  - docId: record `id` (uuid)
 *  - onText: (text) => void   — called with the new text on success
 *  - label: optional button label (default "Regenerate with AI")
 *  - testId: optional data-testid override
 */
export default function LlmRegenerateButton({ collection, docId, onText, label, testId }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!docId) {
      toast.error("Save the record first before regenerating");
      return;
    }
    if (!window.confirm("Regenerate this text via Gemini? The existing text will be overwritten in the database.")) {
      return;
    }
    setLoading(true);
    toast.info("Generating with Gemini — usually 5-15 seconds…");
    try {
      const res = await fetch(
        `${API}/admin/seo/regenerate/${collection}/${docId}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(`Regenerated (${(data.text || "").split(/\s+/).length} words). Saved to DB.`);
      if (typeof onText === "function") onText(data.text || "");
    } catch (e) {
      toast.error(`Regenerate failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={run}
      disabled={loading || !docId}
      className="border-[#2BAEE5] text-[#2BAEE5] hover:bg-[#2BAEE5]/10"
      data-testid={testId || `llm-regenerate-${collection}`}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Sparkles className="w-4 h-4 mr-2" />
      )}
      {label || "Regenerate with AI"}
    </Button>
  );
}
